import { Box, Button, Flex, FormControl, FormErrorMessage, HStack, Input } from '@chakra-ui/react';
import { TextBlock } from '@common/elements/TextBlock';
import { useAsyncError } from '@common/hooks/useAsyncError';
import { isValidEmail } from '@common/utility/validations';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { ROUTES } from 'src/apps/invest/config';
import { useSignUpModel } from '../../models/SignUpModel';
import { PageHeader } from './PageHeader';

type FormValues = { email: string; hiddenEmail: string };

export const EmailForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { email, setEmail } = useSignUpModel();
  const throwError = useAsyncError();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<FormValues>();

  const onSubmit = handleSubmit(async (data) => {
    if (data.hiddenEmail) return throwError(new Error('Spam bot detected, hiddenEmail field should be empty'));
    setIsLoading(true);
    const result = await setEmail(data.email);
    if (result.error) setError('email', { message: result.error });
    setIsLoading(false);
  });

  return (
    <Flex direction={'column'} gap={10} align="center">
      {/* Header */}
      <PageHeader
        title="Let's get started with your email address"
        subtitle="This process should only take a few minutes."
        step={1}
        steps={4}
      />

      {/* Form */}
      <Box as="form" onSubmit={onSubmit} minW={['100%', '350px']}>
        <FormControl
          isInvalid={Boolean(errors.email)}
          mb={5}
          display="flex"
          flexDirection={'column'}
          alignItems={'center'}
          justifyContent={'center'}
          maxWidth={'500px'}>
          <Input
            id="email"
            mb={3}
            placeholder="me@example.com"
            maxW={['full', 350]}
            {...register('email', {
              required: 'Please enter an email',
              validate: (v) => isValidEmail(v) || 'Please use a valid email address',
            })}
            defaultValue={email}
            autoFocus={true}
          />
          <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
        </FormControl>
        <FormControl display="none">
          <Input {...register('hiddenEmail')} />
        </FormControl>
        <Button isDisabled={Boolean(errors.email) || isLoading} isLoading={isLoading} type="submit" w="full" p={7}>
          Continue
        </Button>
      </Box>
      <HStack spacing="1" justify="center" align={'center'}>
        <TextBlock style={{ fontSize: '16px', lineHeight: '16px' }} color="muted">
          Already have an account?
        </TextBlock>
        <Button as={Link} h="16px" variant="link" to={ROUTES.signin}>
          Sign in
        </Button>
      </HStack>
    </Flex>
  );
};
